import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { IssueRow } from "#app/lib/data";

/**
 * Flat selection state for the issue board: the id set driving the card
 * checkboxes, plus an accumulated id→row map. The map exists because board
 * columns window and evict pages — a bulk action (or a multi-card drop) still
 * needs the row after its card unmounted. Same contract as the DataTable's
 * accumulated `selectedRowData`: a stable ref, mutated alongside the set and
 * refreshed from each selected card's current row.
 */
export function useBoardSelection() {
  const { toggle: toggleRow, refresh: refreshRow, ...selection } = useRowSelection<IssueRow>();
  const toggle = useCallback(
    (issue: IssueRow, selected: boolean) => toggleRow(issue.issueId, issue, selected),
    [toggleRow],
  );
  const refresh = useCallback((issue: IssueRow) => refreshRow(issue.issueId, issue), [refreshRow]);
  return { ...selection, toggle, refresh };
}

export function useRowSelection<Row>() {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const rowDataRef = useRef(new Map<string, Row>());

  const toggle = useCallback((id: string, row: Row, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        rowDataRef.current.set(id, row);
        next.add(id);
      } else {
        rowDataRef.current.delete(id);
        next.delete(id);
      }
      return next;
    });
  }, []);

  // A selected card hands back its current row once its render commits
  // (`SelectedRowSync`), so the map follows the refetched data instead of
  // the row as it was when the user ticked it. A real change bumps a
  // version, so the board, and the bulk bar it renders, read the new row.
  const [, setVersion] = useState(0);
  const refresh = useCallback((id: string, row: Row) => {
    const held = rowDataRef.current.get(id);
    if (!held || held === row) return;
    rowDataRef.current.set(id, row);
    setVersion((v) => v + 1);
  }, []);

  const clear = useCallback(() => {
    rowDataRef.current.clear();
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);

  return {
    selectedIds,
    selectedRowData: rowDataRef.current,
    toggle,
    refresh,
    clear,
  };
}

/** Hands a selected card's committed row to the board selection. */
export function SelectedRowSync({
  issue,
  refresh,
}: {
  issue: IssueRow;
  refresh: (issue: IssueRow) => void;
}): null {
  useLayoutEffect(() => refresh(issue), [issue, refresh]);
  return null;
}
